import React, { useState } from 'react';
import { SettingsPage } from '../../../components/SettingsPage/SettingsPage';
import { SettingCard } from '../../../components/SettingCards/SettingCard/SettingCard';
import { PrimaryButton, SecondaryButton } from '../../../components/Buttons/Buttons';
import { CheckboxGroup } from '../../../components/CheckboxGroup/CheckboxGroup';
import { DataTable, DataTableColumn } from '../../../components/DataTable/DataTable';
import { CHECK_CONFIGURATION_SETTING_IDS } from '../../../config/SettingIds';
import { SettingsNativeBridgeFactory } from '../../../bridge/SettingsNativeBridgeFactory';
import { CHECK_CONFIGURATION_PAGE_STRINGS } from '../SupportAndTroubleshootStrings';
import { StatusPassIcon, StatusFailIcon } from '../../../Icons/StatusIcons/StatusIcons';
import { SettingsLogger, logConstants } from '../../../utils/SettingsLogger';
import './CheckConfigurationPage.css';
import { SettingCardContent } from '../../../components/SettingCards/SettingCard/SettingCardContent/SettingCardContent';

interface CheckConfigurationPageProps {
	title: string;
	description?: string;
}

type CheckerType = 'SSONChecker' | 'BeaconChecker' | 'AppPHealthChecker';

const CHECKER_OPTIONS = [
	{ value: 'SSONChecker', label: 'SSONChecker' },
	{ value: 'BeaconChecker', label: 'BeaconChecker' },
	{ value: 'AppPHealthChecker', label: 'AppPHealthChecker' },
];

export interface ConfigurationCheckResultItem {
	status: 'Pass' | 'Fail';
	provider: string;
	suite: string;
	test: string;
	details: string;
	timestamp: string;
}

export interface ConfigurationCheckReport {
	[provider: string]: ConfigurationCheckResultItem[];
}

export const CheckConfigurationPage: React.FC<CheckConfigurationPageProps> = ({
	title,
}) => {
	const [selectedCheckers, setSelectedCheckers] = useState<Set<CheckerType>>(new Set());
	const [isRunning, setIsRunning] = useState(false);
	const [results, setResults] = useState<ConfigurationCheckResultItem[]>([]);

	const handleCheckboxChange = (checker: CheckerType) => {
		setSelectedCheckers((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(checker)) {
				newSet.delete(checker);
			} else {
				newSet.add(checker);
			}
			return newSet;
		});
	};

	const handleRun = async () => {
		setIsRunning(true);
		setResults([]);

		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const response = await bridge.setSetting({
				settingKey: CHECK_CONFIGURATION_SETTING_IDS.RUN_CHECK,
				settingValue: JSON.stringify({
					checkers: Array.from(selectedCheckers),
				}),
			});

			if (response?.data?.settingValue) {
				const parsedResults = JSON.parse(response.data.settingValue);
				if (Array.isArray(parsedResults)) {
					setResults(parsedResults);
				}
			}
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Error running configuration check: ${error}`
			);
		} finally {
			setIsRunning(false);
		}
	};

	const handleSaveReport = async () => {
		try {
			// Group results by provider
			const reportData: ConfigurationCheckReport = {};
			results.forEach((result) => {
				if (!reportData[result.provider]) {
					reportData[result.provider] = [];
				}
				reportData[result.provider].push({
					status: result.status,
					provider: result.provider,
					suite: result.suite,
					test: result.test,
					details: result.details,
					timestamp: result.timestamp,
				});
			});

			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: CHECK_CONFIGURATION_SETTING_IDS.SAVE_REPORT,
				settingValue: JSON.stringify(reportData),
			});

			SettingsLogger.getInstance().write(logConstants.INFO, 'Report saved successfully');
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Error saving report: ${error}`
			);
		}
	};

	const isRunDisabled = selectedCheckers.size === 0 || isRunning;

	const resultColumns: DataTableColumn<ConfigurationCheckResultItem>[] = [
		{
			key: 'status',
			header: CHECK_CONFIGURATION_PAGE_STRINGS.STATUS,
			width: '60px',
			render: (row) => (
				<span
					className={`check-configuration-status-icon ${
						row.status === 'Pass'
							? 'check-configuration-status-pass'
							: 'check-configuration-status-fail'
					}`}
					aria-label={row.status}
				>
					{row.status === 'Pass' ? <StatusPassIcon /> : <StatusFailIcon />}
				</span>
			),
		},
		{
			key: 'provider',
			header: CHECK_CONFIGURATION_PAGE_STRINGS.PROVIDER,
			render: (row) => `${row.provider} | ${row.timestamp}`,
		},
		{ key: 'suite', header: CHECK_CONFIGURATION_PAGE_STRINGS.SUITE },
		{ key: 'test', header: CHECK_CONFIGURATION_PAGE_STRINGS.TEST },
		{ key: 'details', header: CHECK_CONFIGURATION_PAGE_STRINGS.DETAILS },
	];

	return (
		<SettingsPage
			title={title}
			settings={
				<>
					<p className="check-configuration-page-description">
						{CHECK_CONFIGURATION_PAGE_STRINGS.PAGE_DESCRIPTION}
					</p>
					{!isRunning && (
						<SettingCard
							settingId="check-configuration-checkers"
							title={CHECK_CONFIGURATION_PAGE_STRINGS.SELECT_CHECK_TYPE}
							content={
								<SettingCardContent className="check-configuration-content">
									<CheckboxGroup
										options={CHECKER_OPTIONS}
										selectedValues={selectedCheckers}
										onChange={(value) => handleCheckboxChange(value as CheckerType)}
									/>
									<PrimaryButton
										className="check-configuration-run-button"
										onClick={handleRun}
										disabled={isRunDisabled}
										ariaLabel={CHECK_CONFIGURATION_PAGE_STRINGS.RUN_CONFIGURATION_CHECK}
									>
										{CHECK_CONFIGURATION_PAGE_STRINGS.RUN}
									</PrimaryButton>
								</SettingCardContent>
							}
						/>
					)}
					{isRunning && (
						<SettingCard
							settingId="check-configuration-progress"
							title=""
							content={
								<SettingCardContent className="check-configuration-progress">
									<div className="check-configuration-progress-bar-container">
										<div className="check-configuration-progress-bar">
											<div className="check-configuration-progress-bar-fill" />
										</div>
									</div>
									<p className="check-configuration-progress-text">
										{CHECK_CONFIGURATION_PAGE_STRINGS.RUNNING_CONFIGURATION_CHECKER}
									</p>
								</SettingCardContent>
							}
						/>
					)}
					{!isRunning && results.length > 0 && (
						<SettingCard
							settingId="check-configuration-results"
							title=""
							content={
								<SettingCardContent className="check-configuration-results">
									<SecondaryButton
										className="check-configuration-results-header"
										onClick={handleSaveReport}
										ariaLabel={CHECK_CONFIGURATION_PAGE_STRINGS.SAVE_REPORT}
									>
										{CHECK_CONFIGURATION_PAGE_STRINGS.SAVE_REPORT}
									</SecondaryButton>
									<DataTable
										columns={resultColumns}
										data={results}
										keyExtractor={(_row, index) => String(index)}
										ariaLabel="Configuration check results"
									/>
								</SettingCardContent>
							}
						/>
					)}
				</>
			}
		/>
	);
};
